/**
 * Implémentation React-native de l'extraction de document avec OpenCV.js
 * Version optimisée pour mobile Android avec détection améliorée
 */

export interface Point {
  x: number;
  y: number;
}

export interface DocumentScannerResult {
  points: Point[];
  extractedCanvas: HTMLCanvasElement | null;
  confidence: number; // Score de confiance de la détection
}

/**
 * Charge OpenCV.js avec stratégie de fallback
 */
export async function loadOpenCV(): Promise<void> {
  if (window.cv && window.cv.Mat) {
    return;
  }

  // Utiliser directement le fallback minimal pour éviter les problèmes de chargement
  console.log('🔄 Utilisation du fallback OpenCV minimal');
  const { createMinimalOpenCV } = await import('./opencv-fallback');
  createMinimalOpenCV();
  console.log('✅ Fallback OpenCV chargé avec succès');
}

/**
 * Version simplifiée qui utilise le fallback immédiatement
 */
export async function scanDocumentWithFallback(image: HTMLImageElement | HTMLCanvasElement): Promise<DocumentScannerResult | null> {
  try {
    console.log('📄 [SCAN] Début scan avec fallback');
    
    // Utiliser directement le fallback minimal
    const { minimalOpenCV } = await import('./opencv-fallback');
    const result = minimalOpenCV.scanDocument(image);
    
    if (result && result.points.length === 4) {
      console.log('✅ [SCAN] Scan réussi avec fallback');
      return result;
    }
    
    console.warn('⚠️ [SCAN] Aucun document détecté avec fallback');
    return {
      points: [],
      extractedCanvas: null,
      confidence: 0
    };
  } catch (error) {
    console.error('❌ [SCAN] Erreur avec fallback:', error);
    return null;
  }
}

/**
 * Convertit un élément image/canvas en Mat OpenCV
 */
function imageToMat(image: HTMLImageElement | HTMLCanvasElement): any {
  if (!window.cv || !window.cv.imread) {
    throw new Error('OpenCV.js non disponible');
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  if (image instanceof HTMLImageElement) {
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    ctx.drawImage(image, 0, 0);
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
  }

  return window.cv.imread(canvas);
}

/**
 * Convertit une Mat OpenCV en canvas HTML
 */
function matToCanvas(mat: any): HTMLCanvasElement {
  if (!window.cv || !window.cv.imshow) {
    throw new Error('OpenCV.js non disponible');
  }

  const canvas = document.createElement('canvas');
  window.cv.imshow(canvas, mat);
  return canvas;
}

/**
 * Trie les points dans l'ordre : haut-gauche, haut-droit, bas-droit, bas-gauche
 */
function orderPoints(points: Point[]): Point[] {
  // Calculer le centre de masse
  const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
  const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

  // Trier les points par angle par rapport au centre
  const sortedPoints = points.map(p => {
    const angle = Math.atan2(p.y - centerY, p.x - centerX);
    return { point: p, angle };
  }).sort((a, b) => a.angle - b.angle);

  // Trouver le point le plus en haut à gauche (angle le plus négatif)
  const topLeftIndex = sortedPoints.findIndex(p =>
    p.angle >= -Math.PI * 0.75 && p.angle <= -Math.PI * 0.25
  );

  // Réorganiser pour commencer par le coin haut-gauche
  const orderedPoints: Point[] = [];
  for (let i = 0; i < 4; i++) {
    const index = (topLeftIndex + i) % 4;
    orderedPoints.push(sortedPoints[index].point);
  }

  return orderedPoints;
}

/**
 * Calcule la qualité d'un quadrilatère (0-1, 1 = rectangle parfait)
 */
function calculateQuadQuality(points: Point[]): number {
  if (points.length !== 4) return 0;

  // Calculer les 4 angles
  let angleScore = 0;
  for (let i = 0; i < 4; i++) {
    const prev = points[(i + 3) % 4];
    const curr = points[i];
    const next = points[(i + 1) % 4];

    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;

    const dot = dx1 * dx2 + dy1 * dy2;
    const mag1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const mag2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    if (mag1 === 0 || mag2 === 0) continue;

    const cosAngle = dot / (mag1 * mag2);
    const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle))) * (180 / Math.PI);

    // Score basé sur la proximité avec 90°
    const angleDiff = Math.abs(angle - 90);
    angleScore += Math.max(0, 1 - angleDiff / 45);
  }
  angleScore /= 4;

  // Calculer le ratio longueur/largeur pour vérifier la régularité
  const orderedPts = orderPoints(points);
  const width1 = Math.sqrt(Math.pow(orderedPts[1].x - orderedPts[0].x, 2) + Math.pow(orderedPts[1].y - orderedPts[0].y, 2));
  const width2 = Math.sqrt(Math.pow(orderedPts[2].x - orderedPts[3].x, 2) + Math.pow(orderedPts[2].y - orderedPts[3].y, 2));
  const height1 = Math.sqrt(Math.pow(orderedPts[3].x - orderedPts[0].x, 2) + Math.pow(orderedPts[3].y - orderedPts[0].y, 2));
  const height2 = Math.sqrt(Math.pow(orderedPts[2].x - orderedPts[1].x, 2) + Math.pow(orderedPts[2].y - orderedPts[1].y, 2));

  const widthDiff = Math.abs(width1 - width2) / Math.max(width1, width2);
  const heightDiff = Math.abs(height1 - height2) / Math.max(height1, height2);
  const symmetryScore = 1 - (widthDiff + heightDiff) / 2;

  // Score combiné
  return (angleScore * 0.7 + symmetryScore * 0.3);
}

/**
 * Détection optimisée pour mobile avec plusieurs stratégies
 */
async function detectDocumentCornersMobile(src: any): Promise<{ points: Point[], confidence: number } | null> {
  let bestResult: { points: Point[], confidence: number } | null = null;
  let bestScore = 0;

  try {
    console.log('🔍 [MOBILE] Détection optimisée mobile');

    // Redimensionner pour de meilleures performances sur mobile
    const resized = new window.cv.Mat();
    const maxDim = 800;
    const scale = Math.min(1, Math.min(maxDim / src.cols, maxDim / src.rows));
    const newW = Math.round(src.cols * scale);
    const newH = Math.round(src.rows * scale);
    window.cv.resize(src, resized, new window.cv.Size(newW, newH));

    // Conversion en niveaux de gris
    const gray = new window.cv.Mat();
    window.cv.cvtColor(resized, gray, window.cv.COLOR_RGBA2GRAY);

    // Stratégie 1 : Canny avec dilate (meilleure pour bords nets)
    try {
      const blurred1 = new window.cv.Mat();
      window.cv.GaussianBlur(gray, blurred1, new window.cv.Size(5, 5), 0);

      const edges1 = new window.cv.Mat();
      window.cv.Canny(blurred1, edges1, 50, 200, 3, false);

      const dilated = new window.cv.Mat();
      const kernel = window.cv.getStructuringElement(window.cv.MORPH_RECT, new window.cv.Size(5, 5));
      window.cv.dilate(edges1, dilated, kernel);

      const result1 = findBestContour(dilated, resized.rows * resized.cols, scale);
      if (result1 && result1.confidence > bestScore) {
        bestResult = result1;
        bestScore = result1.confidence;
      }

      blurred1.delete();
      edges1.delete();
      dilated.delete();
      kernel.delete();
    } catch (e) {
      console.warn('⚠️ Stratégie 1 échouée:', e);
    }

    // Stratégie 2 : CLAHE + Canny (meilleure pour faible contraste)
    try {
      const clahe = new window.cv.CLAHE(4.0, new window.cv.Size(8, 8));
      const enhanced = new window.cv.Mat();
      clahe.apply(gray, enhanced);

      const blurred2 = new window.cv.Mat();
      window.cv.GaussianBlur(enhanced, blurred2, new window.cv.Size(5, 5), 0);

      const edges2 = new window.cv.Mat();
      window.cv.Canny(blurred2, edges2, 30, 150, 3, false);

      const result2 = findBestContour(edges2, resized.rows * resized.cols, scale);
      if (result2 && result2.confidence > bestScore) {
        bestResult = result2;
        bestScore = result2.confidence;
      }

      clahe.delete();
      enhanced.delete();
      blurred2.delete();
      edges2.delete();
    } catch (e) {
      console.warn('⚠️ Stratégie 2 échouée:', e);
    }

    // Stratégie 3 : Seuillage adaptatif (meilleure pour documents sur fond complexe)
    try {
      const thresh = new window.cv.Mat();
      window.cv.adaptiveThreshold(
        gray, thresh, 255,
        window.cv.ADAPTIVE_THRESH_GAUSSIAN_C,
        window.cv.THRESH_BINARY,
        11, 2
      );

      const morph = new window.cv.Mat();
      const kernel2 = window.cv.getStructuringElement(window.cv.MORPH_RECT, new window.cv.Size(5, 5));
      window.cv.morphologyEx(thresh, morph, window.cv.MORPH_CLOSE, kernel2);

      const result3 = findBestContour(morph, resized.rows * resized.cols, scale);
      if (result3 && result3.confidence > bestScore) {
        bestResult = result3;
        bestScore = result3.confidence;
      }

      thresh.delete();
      morph.delete();
      kernel2.delete();
    } catch (e) {
      console.warn('⚠️ Stratégie 3 échouée:', e);
    }

    resized.delete();
    gray.delete();

    if (bestResult && bestResult.confidence > 0.3) {
      console.log(`✅ [MOBILE] Document détecté (confiance: ${(bestResult.confidence * 100).toFixed(1)}%)`);
      return bestResult;
    }

    console.warn('⚠️ [MOBILE] Aucun document détecté avec confiance suffisante');
    return null;

  } catch (error) {
    console.error('❌ [MOBILE] Erreur détection:', error);
    return null;
  }
}

/**
 * Trouve le meilleur contour rectangulaire dans une image de bords
 */
function findBestContour(edges: any, imageArea: number, scale: number): { points: Point[], confidence: number } | null {
  const contours = new window.cv.MatVector();
  const hierarchy = new window.cv.Mat();

  try {
    window.cv.findContours(edges, contours, hierarchy, window.cv.RETR_LIST, window.cv.CHAIN_APPROX_SIMPLE);

    let bestContour: any = null;
    let bestScore = 0;
    let bestPoints: Point[] = [];

    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i);
      const area = window.cv.contourArea(contour);

      // Filtrer: au moins 15% de l'image, max 95%
      if (area < imageArea * 0.15 || area > imageArea * 0.95) {
        contour.delete();
        continue;
      }

      const perimeter = window.cv.arcLength(contour, true);
      const approx = new window.cv.Mat();

      // Essayer plusieurs tolérances d'approximation
      for (const epsilon of [0.02, 0.03, 0.04]) {
        window.cv.approxPolyDP(contour, approx, epsilon * perimeter, true);

        if (approx.rows === 4) {
          // Vérifier la convexité
          if (!window.cv.isContourConvex(approx)) {
            continue;
          }

          // Extraire les points
          const points: Point[] = [];
          for (let j = 0; j < 4; j++) {
            const pt = approx.data32S.subarray(j * 2, j * 2 + 2);
            points.push({
              x: Math.round(pt[0] / scale),
              y: Math.round(pt[1] / scale)
            });
          }

          // Calculer le score de qualité
          const quality = calculateQuadQuality(points);
          const areaRatio = area / imageArea;

          // Score combiné: qualité géométrique + taille relative
          const score = quality * 0.7 + Math.min(areaRatio, 0.8) * 0.3;

          if (score > bestScore) {
            bestScore = score;
            bestPoints = points;
          }

          break; // Une bonne approximation trouvée
        }
      }

      approx.delete();
      contour.delete();
    }

    contours.delete();
    hierarchy.delete();

    if (bestPoints.length === 4 && bestScore > 0) {
      return {
        points: orderPoints(bestPoints),
        confidence: bestScore
      };
    }

    return null;

  } catch (error) {
    contours.delete();
    hierarchy.delete();
    console.error('❌ Erreur findBestContour:', error);
    return null;
  }
}

/**
 * Extrait un document avec transformation de perspective optimisée
 */
async function extractDocumentFromMat(src: any, points: Point[]): Promise<HTMLCanvasElement | null> {
  try {
    console.log('✂️ [EXTRACT] Début extraction');

    if (points.length !== 4) {
      src.delete();
      return null;
    }

    const orderedPoints = orderPoints(points);

    // Calculer les dimensions du document de sortie
    const widthTop = Math.sqrt(
      Math.pow(orderedPoints[1].x - orderedPoints[0].x, 2) +
      Math.pow(orderedPoints[1].y - orderedPoints[0].y, 2)
    );
    const widthBottom = Math.sqrt(
      Math.pow(orderedPoints[2].x - orderedPoints[3].x, 2) +
      Math.pow(orderedPoints[2].y - orderedPoints[3].y, 2)
    );
    const heightLeft = Math.sqrt(
      Math.pow(orderedPoints[3].x - orderedPoints[0].x, 2) +
      Math.pow(orderedPoints[3].y - orderedPoints[0].y, 2)
    );
    const heightRight = Math.sqrt(
      Math.pow(orderedPoints[2].x - orderedPoints[1].x, 2) +
      Math.pow(orderedPoints[2].y - orderedPoints[1].y, 2)
    );

    const outputWidth = Math.max(widthTop, widthBottom);
    const outputHeight = Math.max(heightLeft, heightRight);

    // Limiter les dimensions pour éviter les images trop grandes sur mobile
    const maxOutputDim = 2000;
    let finalWidth = outputWidth;
    let finalHeight = outputHeight;

    if (outputWidth > maxOutputDim || outputHeight > maxOutputDim) {
      const scaleDown = Math.min(maxOutputDim / outputWidth, maxOutputDim / outputHeight);
      finalWidth = Math.round(outputWidth * scaleDown);
      finalHeight = Math.round(outputHeight * scaleDown);
    }

    // Définir les points source et destination
    const srcPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
      orderedPoints[0].x, orderedPoints[0].y,
      orderedPoints[1].x, orderedPoints[1].y,
      orderedPoints[2].x, orderedPoints[2].y,
      orderedPoints[3].x, orderedPoints[3].y
    ]);

    const dstPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
      0, 0,
      finalWidth, 0,
      finalWidth, finalHeight,
      0, finalHeight
    ]);

    // Transformation de perspective
    const transform = window.cv.getPerspectiveTransform(srcPoints, dstPoints);
    const dst = new window.cv.Mat();

    window.cv.warpPerspective(
      src, dst, transform,
      new window.cv.Size(finalWidth, finalHeight),
      window.cv.INTER_LINEAR,
      window.cv.BORDER_CONSTANT,
      new window.cv.Scalar(255, 255, 255, 255)
    );

    // Amélioration optionnelle de la netteté
    const sharpened = new window.cv.Mat();
    const kernel = window.cv.matFromArray(3, 3, window.cv.CV_32FC1, [
      0, -1, 0,
      -1, 5, -1,
      0, -1, 0
    ]);
    window.cv.filter2D(dst, sharpened, window.cv.CV_8U, kernel);

    const resultCanvas = matToCanvas(sharpened);

    // Nettoyage
    src.delete();
    srcPoints.delete();
    dstPoints.delete();
    transform.delete();
    dst.delete();
    sharpened.delete();
    kernel.delete();

    console.log(`✅ [EXTRACT] Extraction terminée: ${finalWidth}x${finalHeight}px`);
    return resultCanvas;

  } catch (error) {
    console.error('❌ [EXTRACT] Erreur extraction:', error);
    if (src) src.delete();
    return null;
  }
}

/**
 * Fonction principale de scan optimisée pour mobile
 */
export async function scanDocument(image: HTMLImageElement | HTMLCanvasElement): Promise<DocumentScannerResult | null> {
  // Utiliser directement la version avec fallback
  return await scanDocumentWithFallback(image);
}

/**
 * Extraction simple avec retry
 */
export async function extractDocumentWithRetry(
  image: HTMLImageElement | HTMLCanvasElement,
  maxAttempts: number = 2
): Promise<HTMLCanvasElement | null> {

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.log(`🔄 Tentative ${attempt}/${maxAttempts}`);

    try {
      const result = await scanDocument(image);

      if (result?.extractedCanvas && result.confidence > 0.4) {
        console.log(`✅ Succès tentative ${attempt} (confiance: ${(result.confidence * 100).toFixed(1)}%)`);
        return result.extractedCanvas;
      }

      console.log(`⚠️ Tentative ${attempt} : confiance insuffisante (${(result?.confidence || 0) * 100}%)`);

    } catch (error) {
      console.warn(`⚠️ Erreur tentative ${attempt}:`, error);
    }

    if (attempt < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  console.warn('❌ Toutes les tentatives ont échoué');
  return null;
}

/**
 * Version simplifiée pour extraction directe
 */
export async function extractDocumentSimple(
  image: HTMLImageElement | HTMLCanvasElement
): Promise<HTMLCanvasElement | null> {
  const result = await scanDocument(image);
  return result?.extractedCanvas || null;
}

/**
 * Détection seule (sans extraction) pour prévisualisation
 */
export async function detectDocumentCorners(
  image: HTMLImageElement | HTMLCanvasElement
): Promise<Point[] | null> {
  try {
    // Utiliser directement le fallback minimal
    const { minimalOpenCV } = await import('./opencv-fallback');
    const points = minimalOpenCV.detectDocumentCorners(image);
    return points;
  } catch (error) {
    console.error('❌ Erreur détection coins:', error);
    return null;
  }
}